import { Fragment, useEffect, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { Grid3X3, CheckCircle2 } from "lucide-react";

export default function TitanGrid() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // {row, col}
  const [guess, setGuess] = useState("");
  const [sugs, setSugs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setData((await fz.grid.daily()).data); } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (guess.trim().length < 2) { setSugs([]); return; }
    const t = setTimeout(async () => {
      try { setSugs((await fz.grid.search(guess.trim())).data || []); } catch { /* silent */ }
    }, 200);
    return () => clearTimeout(t);
  }, [guess]);

  if (!data) return <div className="titans-card p-6 text-titans-silver text-sm">Loading grid…</div>;

  const submit = async (name) => {
    if (!active || busy) return;
    setBusy(true);
    try {
      const { data: r } = await fz.grid.guess(active.row, active.col, name);
      setData((d) => ({ ...d, cells: r.cells, guesses_left: r.guesses_left, completed: r.completed }));
      if (r.correct) toast.success(`${r.player} — correct!`);
      else toast.error("Not a match for that square");
      if (r.award) toastAward(r.award);
      setActive(null); setGuess(""); setSugs([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Guess failed");
    } finally { setBusy(false); }
  };

  const solved = Object.keys(data.cells || {}).length;

  return (
    <div className="titans-card p-5 md:p-6" data-testid="fz-grid">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Grid3X3 size={16} className="text-titans-blue" />
          <span className="font-display text-xl uppercase">Daily Titan Grid</span>
        </div>
        <span className="font-cond uppercase tracking-wider text-xs text-titans-silver" data-testid="fz-grid-guesses">
          {data.guesses_left} guesses left · {solved}/9
        </span>
      </div>

      <div className="grid gap-1.5" style={{ gridTemplateColumns: "minmax(72px, 1.2fr) repeat(3, 1fr)" }}>
        <div />
        {data.cols.map((c) => (
          <div key={c.key} className="text-center font-cond uppercase tracking-wider text-[10px] md:text-[11px] text-titans-blue px-1 pb-1">{c.label}</div>
        ))}
        {data.rows.map((r, ri) => (
          <Fragment key={r.key}>
            <div className="flex items-center font-cond uppercase tracking-wider text-[10px] md:text-[11px] text-titans-silver pr-1">{r.label}</div>
            {data.cols.map((c, ci) => {
              const cell = data.cells?.[`${ri}-${ci}`];
              const isActive = active && active.row === ri && active.col === ci;
              return (
                <button
                  key={`${ri}-${ci}`}
                  disabled={!!cell || data.completed || data.guesses_left <= 0}
                  onClick={() => { setActive({ row: ri, col: ci }); setGuess(""); setSugs([]); }}
                  data-testid={`fz-grid-cell-${ri}-${ci}`}
                  className={`aspect-square md:aspect-[4/3] rounded-lg border text-center px-1 transition-all duration-200 ${
                    cell
                      ? "bg-titans-navy border-titans-navy text-white"
                      : isActive
                        ? "border-titans-red bg-titans-red/10 scale-[1.03]"
                        : "border-[#E2E8F0] bg-[#F1F5F9] hover:border-titans-blue"
                  }`}
                >
                  {cell ? (
                    <span className="font-cond uppercase tracking-wide text-[10px] md:text-xs leading-tight block px-0.5">{cell.name}</span>
                  ) : (
                    <span className="text-titans-muted text-lg">＋</span>
                  )}
                </button>
              );
            })}
          </Fragment>
        ))}
      </div>

      {active && !data.completed && (
        <div className="mt-4 relative" data-testid="fz-grid-input-wrap">
          <input
            autoFocus
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && guess.trim()) submit(guess.trim()); }}
            placeholder={`${data.rows[active.row].label} × ${data.cols[active.col].label} — type a player…`}
            data-testid="fz-grid-input"
            className="w-full bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-4 py-3 font-body text-sm"
          />
          {sugs.length > 0 && (
            <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-xl overflow-hidden">
              {sugs.map((s) => (
                <button key={s} onClick={() => submit(s)} data-testid={`fz-grid-sug-${s.replace(/\s+/g, "-").toLowerCase()}`} className="block w-full text-left px-4 py-2.5 font-body text-sm text-[#10233B] hover:bg-[#F1F5F9]">
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {data.completed && (
        <div className="mt-4 flex items-center gap-2 border-l-4 border-titans-blue bg-titans-blue/10 p-3" data-testid="fz-grid-done">
          <CheckCircle2 size={16} className="text-titans-blue" />
          <span className="font-cond uppercase tracking-wider text-xs">
            {solved === 9 ? "IMMACULATE — perfect grid!" : `Finished: ${solved}/9 squares.`} New grid tomorrow.
          </span>
        </div>
      )}
    </div>
  );
}
